import {
  AuthAction,
  AuthActionTypes,
  AuthState,
  authReducer,
} from '../reducer/authReducer';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import React, { useContext, useEffect, useState } from 'react';
import {
  graphQLClient,
  updateClientAPIWithRegion,
  updateClientGraphQLWithRegion,
} from '@devd-client/api';

import {
  Loader,
  useToastHook,
  useDashboardStore,
  useTeamComparisonStore,
  useMetricsTableStore,
  useDetailsPageStore,
  useSidebarStore,
  useGlobalState,
} from '@devd-client/devd/components';
import { auth } from '../helpers/auth';
import {
  absoluteClearInterval,
  absoluteSetInterval,
} from '../helpers/authUtils';
import { getUser } from '../api/endpoints';
import { useFetchOrdinals } from '../api';
import { useAuthStore } from '../store/authStore';
export interface AuthProps {
  children?: React.ReactNode;
}

const initState = {
  user: null,
  authIsReady: false,
};

const nan = () => {
  return;
};

export const AuthContext = React.createContext<
  [AuthState, React.Dispatch<AuthAction>, () => void]
>([initState, nan, nan]);

export const AuthProvider = ({ children }: AuthProps) => {
  const [, setToken] = useState(null);
  const [newToast] = useToastHook();
  const [pending, setPending] = useState(true);
  const [state, dispatch] = React.useReducer(authReducer, initState);
  const metricTableFilters = useMetricsTableStore();

  const handleLogout = async () => {
    // eslint-disable-next-line no-debugger
    dispatch({ type: AuthActionTypes.LOGOUT });

    // Clear the centralized auth store
    useAuthStore.getState().clearAuth();

    useDashboardStore.setState({
      selectedTeam: {
        value: '',
        label: 'Organisation',
      },
      selected: {
        id: 0,
        startDate: '',
        endDate: '',
        duration: '',
        sprintId: '',
      },
      filterTitle: '',
      active: '',
    });
    useDetailsPageStore.setState({
      selected: {
        id: 0,
        startDate: '',
        endDate: '',
        duration: '',
        sprintId: '',
      },
      filterTitle: '',
      active: '',
    });
    useTeamComparisonStore.setState({
      selectedTeams: [],
      selectedMetrics: [],
      isChecked: true,
    });

    useSidebarStore.setState({
      open: true,
      hideBanner: false,
    });
    useGlobalState.setState({
      globalState: {},
    });

    localStorage.removeItem('deliverables-view');
    metricTableFilters.reset();
    localStorage.removeItem('hiddenContributorTableColumns');
    localStorage.removeItem('contributorTableFilters');
    updateClientGraphQLWithRegion(null);
    updateClientAPIWithRegion(null);
    localStorage.clear();
    sessionStorage.setItem('produceReturnUrl', 'false');
    await auth.signOut();
  };

  useEffect(() => {
    let isAvailable = true;
    dispatch({ type: AuthActionTypes.LOGOUT });

    const handleAuthStateChanged = async (user: any) => {
      if (!isAvailable) return;
      if (!user) {
        setToken(null);
        useAuthStore.getState().setAuth({ token: null });
        setPending(false);
        return;
      }

      try {
        const idTokenResult = await user.getIdTokenResult(true);

        if (
          !idTokenResult.claims.orgId &&
          window.location.pathname.includes('/account/login')
        ) {
          newToast({
            message:
              'This user is not registered with any organization. Please signup at https://devdynamics.ai/signup',
            status: 'error',
          });
          handleLogout();
          return;
        }

        if (
          !idTokenResult.claims.orgId &&
          window.location.pathname.includes('/invitation')
        ) {
          return;
        }

        const {
          token,
          claims: { orgId, role, auth_time },
        } = idTokenResult;

        if (
          token &&
          orgId &&
          [
            'SUPPORT',
            'EMPLOYER_ADMIN',
            'EMPLOYER_READ',
            'EMPLOYER_WRITE',
            'EMPLOYEE',
            'MANAGER',
          ].includes(role)
        ) {
          // Set initial auth data in centralized store
          useAuthStore.getState().setAuth({
            token,
            authId: user.uid,
            role,
          });

          graphQLClient.setHeader('authorization', `bearer ${token}`);
          setToken(token);

          try {
            const userResponse = await getUser();
            const {
              dto: { org_id, org_name, id, team, region, title },
            } = userResponse;

            // Update centralized store with user data
            useAuthStore.getState().setAuth({
              orgId: org_id,
              orgName: org_name,
              userId: id,
              team: team || null,
              region: region || null,
              userTitle: title || null,
            });

            updateClientGraphQLWithRegion(region);
            updateClientAPIWithRegion(region);

            dispatch({
              type: AuthActionTypes.AUTH_READY,
              payload: { ...user, ...userResponse },
            });
            useGlobalState.setState({
              globalState: {
                ...user,
                ...userResponse,
              },
            });
          } catch (error: any) {
            newToast({
              message: error?.message || 'Error in fetching user data',
              status: 'error',
            });
            handleLogout();
          } finally {
            setPending(false);
          }

          const authTime = auth_time * 1000;
          const sessionDurationInMilliseconds = 60 * 60 * 1000; // 60 minutes
          const expirationInMilliseconds =
            sessionDurationInMilliseconds - (Date.now() - authTime);
        } else {
          handleUnauthorizedUser();
        }
      } catch (error: any) {
        console.log('Error found in fetching token:', error);
        newToast({
          message: error?.message || 'Error in fetching token',
          status: 'error',
        });
      }
    };

    const handleUnauthorizedUser = () => {
      setPending(false);
      setToken(null);
      handleLogout();
      newToast({
        message: 'User not authorised.',
        status: 'error',
      });
    };

    auth.onIdTokenChanged(handleAuthStateChanged);

    return () => {
      isAvailable = false;
    };
  }, []);

  useEffect(() => {
    const handle = absoluteSetInterval(async () => {
      const user = auth.currentUser;
      if (user) {
        try {
          const token = await user.getIdToken(true);

          // Update centralized store
          useAuthStore.getState().setAuth({ token });

          graphQLClient.setHeader('authorization', `bearer ${token}`);
        } catch (error) {
          handleLogout();
        }
      }
    }, 55 * 60 * 1000);

    return () => absoluteClearInterval(handle);
  }, []);

  if (pending) {
    return <Loader />;
  }

  return (
    <AuthContext.Provider value={[state, dispatch, handleLogout]}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};

declare global {
  interface Window {
    Intercom: any;
    analytics: any;
  }
}

export function RequireAuth({ children }: { children: JSX.Element }) {
  const [appState, , handleLogout] = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { data } = useFetchOrdinals();

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return;
    window.analytics.identify(appState?.user?.dto?.id, {
      firstName: appState?.user?.dto?.firstName,
      lastName: appState?.user?.dto?.lastName,
      email: appState?.user?.email,
      orgId: appState?.user?.dto?.org_id,
    });
  }, [location]);

  useEffect(() => {
    if (
      data?.dto?.setupSteps.some(
        (item: any) =>
          item.accountIntegrationStatus === 'FAILED' ||
          item.accountIntegrationStatus === 'INIT'
      )
    ) {
      navigate('/onboarding');
    }
  }, [data]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return;
    setTimeout(() => {
      window.Intercom('boot', {
        api_base: 'https://api-iam.intercom.io',
        app_id: 'diiw5xyw',
        user_id: appState?.user?.dto?.id,
        name: appState?.user?.displayName,
        email: appState?.user?.email,
        created_at: appState?.user?.metadata?.createdAt,
      });
    }, 5000);

    return () => window.Intercom('shutdown');
  }, []);

  if (!appState.user) {
    if (location.pathname !== '/account/login') {
      const currentUrl = window.location.href;
      const fullUrl = currentUrl.replace(window.location.origin, '');

      if (
        !fullUrl.includes('/account/login') &&
        sessionStorage.getItem('produceReturnUrl') !== 'false'
      ) {
        sessionStorage.setItem('returnUrl', fullUrl);
      }
    }

    return <Navigate to="/account/login" state={{ from: location }} replace />;
  }

  if (sessionStorage.getItem('returnUrl')) {
    sessionStorage.removeItem('returnUrl');
  }

  if (sessionStorage.getItem('produceReturnUrl')) {
    sessionStorage.removeItem('produceReturnUrl');
  }

  return children;
}
